import { useState, useMemo, useCallback } from 'react'
import type { ChangeEvent } from 'react'
import { useIntl } from 'react-intl'
import Pane from 'components/Common/Pane'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import Heading from 'LuxKit/Typography/Heading'
import TextButton from 'LuxKit/Button/TextButton'
import SwitchButton from 'LuxKit/Switch/SwitchButton'

import config from 'constants/config'
import { useAppSelector } from 'hooks/reduxHooks'
import ExpandableObjectView from '../ExpandableObjectView'
import Group from 'components/utils/Group'
import BodyText from 'LuxKit/Typography/BodyText'
import { useChannelMarkupEvaluationEnabled, setChannelMarkupEvaluationMode } from 'components/App/ChannelMarkupEvaluation/useChannelMarkupEvaluationEnabled'

function PromoTab() {
  const intl = useIntl()
  const [showChannelMarkupEvaluationToggle, setShowChannelMarkupEvaluationToggle] = useState(true)

  const marketingState = useAppSelector(state => state.marketing)
  const voucherState = useMemo(() => ({
    vouchers: marketingState.vouchers,
    voucherConditionResults: marketingState.voucherConditionResults,
    allVouchers: marketingState.allVouchers,
    allVouchersStatus: marketingState.allVouchersStatus,
    isFetchingAllVouchers: marketingState.isFetchingAllVouchers,
    fetchingVouchersError: marketingState.fetchingVouchersError,
  }), [
    marketingState.allVouchers,
    marketingState.allVouchersStatus,
    marketingState.fetchingVouchersError,
    marketingState.isFetchingAllVouchers,
    marketingState.voucherConditionResults,
    marketingState.vouchers,
  ])
  const voucherConditionResults = voucherState.voucherConditionResults
  const channelMarkupState = useMemo(() => ({
    channelMarkup: marketingState.channelMarkup,
    fetching: marketingState.fetching,
  }), [marketingState.channelMarkup, marketingState.fetching])
  const channelMarkupConfigState = useMemo(() => ({
    enabled: config.ENABLE_CHANNEL_MARKUP,
    marketingRouterEnabled: config.MARKETING_ROUTER_ENABLED,
    enabledForMemberPrice: config.ENABLE_CHANNEL_MARKUP_FOR_MEMBER_PRICE,
  }), [])
  const channelMarkupEvaluationEnabled = useChannelMarkupEvaluationEnabled()
  const handleChannelMarkupEvaluationToggleChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    setShowChannelMarkupEvaluationToggle(event.target.checked)
  }, [])

  const toggleChannelMarkupEvaluationMode = () => {
    setChannelMarkupEvaluationMode(!channelMarkupEvaluationEnabled)
  }
  const voucherEvaluationsById = useMemo(() => {
    const map = new Map<string, NonNullable<App.PublicVoucherCalculated['evaluations']>>()
    voucherState.vouchers.forEach((voucher) => {
      if (voucher.evaluations?.length) {
        map.set(voucher.voucherId, voucher.evaluations)
      }
    })
    return map
  }, [voucherState.vouchers])
  const evaluatedVoucherIds = useMemo(() => new Set(voucherEvaluationsById.keys()), [voucherEvaluationsById])
  const voucherEvaluationRows = useMemo(() => voucherState.vouchers.map((voucher) => {
    const evaluations = voucherEvaluationsById.get(voucher.voucherId)
    const passed = evaluations?.every((evaluation) => (
      evaluation.rewardGroup.results.every((result) => result.checkPassed)
    ))

    return { voucher, evaluations, passed }
  }), [voucherEvaluationsById, voucherState.vouchers])
  const fallbackVoucherConditionResults = useMemo(() => (
    voucherConditionResults.filter((result) => !evaluatedVoucherIds.has(result.voucherId))
  ), [evaluatedVoucherIds, voucherConditionResults])

  const heading = intl.formatMessage({
    id: 'jBDvWHsXsQ4FkKh6z_lB',
    defaultMessage: 'Promo',
  })
  const description = intl.formatMessage({
    id: 'k9Vg9g0aYwA_TCWK6zjZ',
    defaultMessage: 'Promo service state grouped by promo, voucher, and channel-markup data.',
  })
  const promoHeading = intl.formatMessage({
    id: 'Dj2F0v0-QCVp1EJ-cjdA',
    defaultMessage: 'Promo',
  })
  const voucherHeading = intl.formatMessage({
    id: 'BDhMhhtUeo_pKbh2Ci41',
    defaultMessage: 'Voucher',
  })
  const evaluationsHeading = intl.formatMessage({
    id: '4Za8zy0yfpzkHmRjOCjh',
    defaultMessage: 'Evaluations',
  })
  const channelMarkupHeading = intl.formatMessage({
    id: 'by54CnnJ_xavfXYVZID-',
    defaultMessage: 'Channel-markup',
  })
  const channelMarkupEvaluationToggleLabel = intl.formatMessage({
    id: 'temp_dev_panel_promo_section_channel_markup_evaluation_toggle_label',
    defaultMessage: 'Show channel markup evaluation toggle',
  })

  return <Pane padding={20}>
    <VerticalSpacer gap={20}>
      <div>
        <Heading variant="heading5">{heading}</Heading>
        <div>{description}</div>
        <div>
          <SwitchButton
            checked={showChannelMarkupEvaluationToggle}
            onChange={handleChannelMarkupEvaluationToggleChange}
          >
            {channelMarkupEvaluationToggleLabel}
          </SwitchButton>
        </div>
      </div>

      <div>
        <Heading variant="heading6">{promoHeading}</Heading>
        <ExpandableObjectView value={marketingState.promoDisplayConfigs} initialExpanded />
      </div>

      <div>
        <Heading variant="heading6">{voucherHeading}</Heading>
        <ExpandableObjectView value={voucherState} initialExpanded />
        <Group direction="vertical" gap={8}>
          {voucherEvaluationRows.map(({ voucher, evaluations, passed }) => (
            <div key={voucher.voucherId}>
              <BodyText variant="small" weight="bold">
                {voucher.voucherName}
              </BodyText>
              <BodyText
                variant="small"
                colour={
                  passed === undefined ? 'warning' : passed ? 'success' : 'critical'
                }
              >
                {evaluations?.length
                  ? `${evaluations.length} evaluation${evaluations.length === 1 ? '' : 's'} attached`
                  : 'No evaluations attached'}
              </BodyText>
            </div>
          ))}
        </Group>
      </div>

      <div>
        <Heading variant="heading6">{evaluationsHeading}</Heading>
        <Group direction="vertical" gap={4}>
          {voucherState.vouchers.flatMap((voucher) => {
            const evaluations = voucherEvaluationsById.get(voucher.voucherId)
            if (!evaluations?.length) {
              return []
            }

            return evaluations.map((evaluation, index) => {
              const passed = evaluation.rewardGroup.results.every((result) => result.checkPassed)

              return <div key={`${voucher.voucherId}-${evaluation.rewardGroup.rewardId}-${index}`}>
                <BodyText variant="small" weight="bold">
                  {voucher.voucherName}: {evaluation.rewardGroup.title}
                </BodyText>
                <Group direction="vertical" gap={2}>
                  {evaluation.rewardGroup.results.map((result, resultIndex) => (
                    <BodyText
                      key={`${voucher.voucherId}-${evaluation.rewardGroup.rewardId}-${result.code ?? 'result'}-${resultIndex}`}
                      variant="small"
                      colour={result.checkPassed ? 'success' : 'critical'}
                    >
                      {result.conditionResultText ?? result.code ?? 'Evaluation result'}
                    </BodyText>
                  ))}
                </Group>
                <BodyText variant="small" colour={passed ? 'success' : 'critical'}>
                  {passed ? 'passed' : 'failed'}
                </BodyText>
              </div>
            })
          })}
          {evaluatedVoucherIds.size === 0 && fallbackVoucherConditionResults.length === 0
            ? (
                <BodyText variant="small" colour="neutral-three">
                  {intl.formatMessage({
                    id: 'lv8bVwSNOwMmO7uCwG6c',
                    defaultMessage: 'No evaluations available.',
                  })}
                </BodyText>
              )
            : fallbackVoucherConditionResults.length > 0
              ? fallbackVoucherConditionResults.map((result, index) => {
                  const passed = result.errors.length === 0
                  return <BodyText
                    key={`${result.voucherId}-${index}`}
                    variant="small"
                    colour={passed ? 'success' : 'critical'}
                  >
                    {result.voucherName}: {passed ? 'passed' : 'failed'} (fallback)
                  </BodyText>
                }) : null}
        </Group>
      </div>

      <div>
        <Heading variant="heading6">{channelMarkupHeading}</Heading>
        {showChannelMarkupEvaluationToggle && <TextButton kind="primary" onClick={toggleChannelMarkupEvaluationMode}>
          {channelMarkupEvaluationEnabled ? 'Hide channel markup evaluations' : 'Show channel markup evaluations'}
        </TextButton>}
        <ExpandableObjectView value={channelMarkupConfigState} initialExpanded />
        <ExpandableObjectView value={channelMarkupState} initialExpanded />
      </div>
    </VerticalSpacer>
  </Pane>
}

export default PromoTab
